import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageShell from "@/components/proof/PageShell";
import { MonoLabel } from "@/components/proof/primitives";
import { easing } from "@/lib/animations";
import { JOURNAL } from "@/data/journal";

const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const JournalCard = ({ post, index, featured }) => (
    <motion.article
        initial={{ opacity: 0, y: 26 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.18 }}
        transition={{
            duration: 0.9,
            delay: 0.08 + (index % 3) * 0.06,
            ease: easing.primary,
        }}
        data-testid={`journal-card-${post.slug}`}
        style={{
            display: "flex",
            flexDirection: "column",
            gap: 14,
            gridColumn: featured ? "1 / -1" : "auto",
        }}
    >
        <Link
            to={`/journal/${post.slug}`}
            style={{
                color: "inherit",
                textDecoration: "none",
                display: "flex",
                flexDirection: "column",
                gap: 14,
            }}
        >
            <div
                style={{
                    aspectRatio: featured ? "16 / 9" : "4 / 3",
                    background: post.bgTone,
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                }}
            >
                <span
                    style={{
                        position: "absolute",
                        top: 14,
                        left: 14,
                        fontFamily: "var(--font-mono)",
                        fontSize: 10,
                        letterSpacing: "0.28em",
                        textTransform: "uppercase",
                        color: "rgba(12,12,12,0.55)",
                    }}
                >
                    {String(index + 1).padStart(2, "0")} ·{" "}
                    {post.readMinutes} min read
                </span>
                <img
                    src={post.cover}
                    alt={post.title}
                    draggable={false}
                    style={{
                        width: featured ? "55%" : "72%",
                        height: featured ? "70%" : "82%",
                        objectFit: "contain",
                        mixBlendMode: "multiply",
                    }}
                />
            </div>
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    paddingTop: 4,
                }}
            >
                <MonoLabel tone="mute">
                    {formatDate(post.publishedAt)} · {post.author}
                </MonoLabel>
                <h3
                    style={{
                        margin: 0,
                        fontFamily: "var(--font-display)",
                        fontWeight: 700,
                        fontSize: featured
                            ? "clamp(28px, 3.6vw, 56px)"
                            : "clamp(20px, 2vw, 28px)",
                        letterSpacing: "-0.025em",
                        lineHeight: 1.05,
                        textTransform: "uppercase",
                        color: "var(--on-ink)",
                    }}
                >
                    {post.title}
                </h3>
                <p
                    style={{
                        margin: 0,
                        fontFamily: "var(--font-serif)",
                        fontStyle: "italic",
                        fontSize: featured ? 17 : 15,
                        lineHeight: 1.55,
                        color: "var(--on-ink-soft)",
                        maxWidth: featured ? "60ch" : "44ch",
                    }}
                >
                    {post.excerpt}
                </p>
            </div>
        </Link>
    </motion.article>
);

export default function Journal() {
    const [featured, ...rest] = JOURNAL;
    return (
        <PageShell
            sectionNumber="14"
            eyebrow="Editorial · The Field Notes"
            title="Journal"
            crumb="Journal"
            data-testid="page-journal"
        >
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "clamp(40px, 5vw, 76px)",
                }}
            >
                <p
                    style={{
                        margin: 0,
                        fontFamily: "var(--font-serif)",
                        fontStyle: "italic",
                        fontSize: "clamp(16px, 1.3vw, 21px)",
                        lineHeight: 1.55,
                        color: "var(--on-ink-soft)",
                        maxWidth: "60ch",
                    }}
                    data-testid="journal-intro"
                >
                    Notes from the atelier. Studies on material, manufacture
                    and the slow art of building a wardrobe that lasts.
                </p>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingBottom: 14,
                        borderBottom: "1px solid var(--on-ink-line)",
                    }}
                >
                    <MonoLabel>{String(JOURNAL.length).padStart(2, "0")} · Entries</MonoLabel>
                    <MonoLabel tone="mute">Updated weekly</MonoLabel>
                </div>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns:
                            "repeat(auto-fill, minmax(320px, 1fr))",
                        gap: "clamp(28px, 3vw, 56px)",
                    }}
                    data-testid="journal-grid"
                >
                    <JournalCard post={featured} index={0} featured />
                    {rest.map((post, i) => (
                        <JournalCard key={post.slug} post={post} index={i + 1} />
                    ))}
                </div>
            </div>
        </PageShell>
    );
}
